import { t } from '@/ui/i18n/i18n';
import { IssueStatus, Status } from '@/entities/todo';
import { Status as ChecklistStatus } from '@/entities/checklist';
import { ValidationError } from '@/entities/validation';
import {
  ApiError,
  CLIENT_NOT_SUPPORTED,
  DECODE_ERROR,
  FETCHING_AUTH_HEADER_ERROR,
  HAS_NO_ACTIVE_SUBSCRIPTION,
  NETWORK_ERROR,
  ORGANIZATION_SUSPENDED,
  PARSE_ERROR,
  QUOTA_EXCEEDED,
  SUBSCRIPTION_INCOMPLETE,
  SUBSCRIPTION_PAST_DUE,
} from '@/services/api';
import { IssuePriority, IssueType } from '@/entities/issue';
import { StatusCodes } from 'http-status-codes';
import { Permissions } from './auth/auth.types';
import { getMappedRecord } from '@/ui/helpers/uiHelper';
import { SelectOption } from '@/ui/types';
import { ApprovalStatus, ApprovalType } from '@/entities/approvals/constants';

export function checklistStatus(value: ChecklistStatus): string {
  const map: Record<ChecklistStatus, string> = {
    [ChecklistStatus.Active]: t('common.active'),
    [ChecklistStatus.Inactive]: t('common.inactive'),
  };

  return getMappedRecord<ChecklistStatus>(map, value);
}

export function issueStatusToText(value: IssueStatus): string {
  const map: Record<IssueStatus, string> = {
    [IssueStatus.Pending]: t('status.pending'),
    [IssueStatus.InProgress]: t('status.in-progress'),
    [IssueStatus.Done]: t('status.done'),
    [IssueStatus.CantDo]: t('status.cant-do'),
  };

  return getMappedRecord<IssueStatus>(map, value);
}

export function approvalStatusToText(value: ApprovalStatus): string {
  const map: Record<ApprovalStatus, string> = {
    [ApprovalStatus.Pending]: t('approval.status.pending'),
    [ApprovalStatus.Approved]: t('approval.status.approved'),
    [ApprovalStatus.Rejected]: t('approval.status.rejected'),
    [ApprovalStatus.ChangesRequested]: t('approval.status.changes-requested'),
  };

  return getMappedRecord<ApprovalStatus>(map, value);
}
export function approvalTypeToText(value: ApprovalType): string {
  const map: Record<ApprovalType, string> = {
    [ApprovalType.Inspection]: t('approval.types.inspection'),
    [ApprovalType.Issue]: t('common.issue'),
  };

  return getMappedRecord<ApprovalType>(map, value);
}

export function todoStatusToText(value: Status): string {
  const map: Record<Status, string> = {
    [Status.Pending]: t('status.pending'),
    [Status.InProgress]: t('status.in-progress'),
    [Status.Done]: t('status.done'),
    //legacy, remove later
    [Status.CantDo]: t('status.cant-do'),
    [Status.SystemRejected]: t('status.system-rejected'),
  };

  return getMappedRecord<Status>(map, value);
}

export function issuePriority(value: IssuePriority): string {
  const map: Record<IssuePriority, string> = {
    [IssuePriority.Low]: t('common.normal'),
    [IssuePriority.Normal]: t('common.normal'),
    [IssuePriority.High]: t('common.high'),
  };

  return getMappedRecord<IssuePriority>(map, value);
}

export function issueType(value: IssueType): string {
  const map: Record<IssueType, string> = {
    [IssueType.AdHoc]: t('common.ad-hoc'),
    [IssueType.Automatic]: t('common.automatic'),
    [IssueType.InForm]: t('issues.type.in-form'),
    [IssueType.Sensor]: t('common.sensor'),
    [IssueType.ManualForm]: t('issues.type.manual-form'),
    [IssueType.AutomaticForm]: t('issues.type.automatic-form'),
  };

  return getMappedRecord<IssueType>(map, value);
}

export function language(value: string): string {
  const map: Record<string, string> = {
    da: t('language.da'),
    de: t('language.de'),
    en: t('language.en'),
    'en-GB': t('language.en-GB'),
    es: t('language.es'),
    fi: t('language.fi'),
    fr: t('language.fr'),
    it: t('language.it'),
    nl: t('language.nl'),
    pl: t('language.pl'),
    'pt-BR': t('language.pt-BR'),
    sv: t('language.sv'),
    tr: t('language.tr'),
    sq: t('language.sq'),
    am: t('language.am'),
    ar: t('language.ar'),
    hy: t('language.hy'),
    bn: t('language.bn'),
    bs: t('language.bs'),
    bg: t('language.bg'),
    my: t('language.my'),
    ca: t('language.ca'),
    zh: t('language.zh'),
    hr: t('language.hr'),
    cs: t('language.cs'),
    et: t('language.et'),
    ka: t('language.ka'),
    el: t('language.el'),
    gu: t('language.gu'),
    hi: t('language.hi'),
    hu: t('language.hu'),
    is: t('language.is'),
    id: t('language.id'),
    ja: t('language.ja'),
    kn: t('language.kn'),
    kk: t('language.kk'),
    ko: t('language.ko'),
    lv: t('language.lv'),
    lt: t('language.lt'),
    mk: t('language.mk'),
    ms: t('language.ms'),
    ml: t('language.ml'),
    mr: t('language.mr'),
    mn: t('language.mn'),
    no: t('language.no'),
    fa: t('language.fa'),
    pa: t('language.pa'),
    ro: t('language.ro'),
    ru: t('language.ru'),
    sr: t('language.sr'),
    sk: t('language.sk'),
    sl: t('language.sl'),
    so: t('language.so'),
    sw: t('language.sw'),
    tl: t('language.tl'),
    ta: t('language.ta'),
    te: t('language.te'),
    th: t('language.th'),
    uk: t('language.uk'),
    ur: t('language.ur'),
    vi: t('language.vi'),
    'zh-CN': t('language.zh-CN'),
  };

  const lang = getMappedRecord<string>(map, value);
  return lang.length > 0 ? lang : t('language.unknown');
}

export function validationError(field: string, error: ValidationError) {
  type Excluded = 'excelMaxRows' | 'maxFile' | 'mime' | 'notFound' | 'multipleValuesNotAllowed' |'invalidOptionValue' |'duplicateId' | 'typeMismatch';

  const map: Record<Exclude<ValidationError['type'], Excluded>, string> = {
    required: t('dictionary.validation.required', { field }),
    in: t('dictionary.validation.in', { field }),
    unique: t('dictionary.validation.unique', { field }),
    exists: t('dictionary.validation.exists', { field }),
    confirmed: t('dictionary.validation.confirmed', {
      field,
      parameter: error.parameters[0],
    }),
    email: t('dictionary.validation.email', { field }),
    phone: t('dictionary.validation.phone', { field }),
    number: t('dictionary.validation.number', { field }),
    numberMin: t('dictionary.validation.numberMin', {
      field,
      parameter: error.parameters[0],
    }),
    numberMax: t('dictionary.validation.numberMax', {
      field,
      parameter: error.parameters[0],
    }),
    stringMin: t('dictionary.validation.stringMin', {
      field,
      parameter: error.parameters[0],
    }),
    stringMax: t('dictionary.validation.stringMax', {
      field,
      parameter: error.parameters[0],
    }),
    notAllowed: t('dictionary.validation.notAllowed'),
    size: t('dictionary.validation.size'),
    strongPassword: t('dictionary.validation.strongPassword'),
    unknown: t('dictionary.validation.unknown', { field }),
  };

  return getMappedRecord<string>(map, error.type);
}

export const permissionsToText = (): Record<Permissions, string> => ({
  [Permissions.MANAGE_BILLING]: t('dictionary.permission.manageBilling'),
  [Permissions.MANAGE_USERS]: t('dictionary.permission.manageUsers'),
  [Permissions.MANAGE_ENTITY_ITEMS]: t('dictionary.permission.manageEntityItems'),
  [Permissions.MANAGE_ENTITY_FOLDERS]: t('dictionary.permission.manageEntityFolders'),
  [Permissions.MANAGE_ENTITY_TYPES]: t('dictionary.permission.manageEntityTypes'),
  [Permissions.MANAGE_ENTITY_PROPERTIES]: t('dictionary.permission.manageEntityProperties'),
  [Permissions.MANAGE_PERMISSIONS]: t('dictionary.permission.managePermissions'),
  [Permissions.MANAGE_CATEGORIES]: t('dictionary.permission.manageCategories'),
  [Permissions.MANAGE_CHECKLISTS]: t('dictionary.permission.manageChecklists'),
  [Permissions.MANAGE_DRAFT_INSPECTIONS]: t('dictionary.permission.manageDraftInspections'),
  [Permissions.CONDUCT_OPEN_INSPECTIONS]: t('dictionary.permission.conductOpenInspections'),
  [Permissions.VIEW_ALL_INSPECTION]: t('dictionary.permission.viewAllInspections'),
  [Permissions.EDIT_OWN_INSPECTION]: t('dictionary.permission.editOwnInspections'),
  [Permissions.EDIT_ALL_INSPECTION]: t('dictionary.permission.editAllInspections'),
  [Permissions.CREATE_ISSUE]: t('dictionary.permission.createIssue'),
  [Permissions.CREATE_ISSUE_WITH_ASSIGNEE]: t('dictionary.permission.createIssueWithAssignee'),
  [Permissions.MANAGE_OWN_ISSUE]: t('dictionary.permission.manageOwnIssues'),
  [Permissions.MANAGE_ASSIGNED_SOLVED_ISSUES]: t('dictionary.permission.manageAssignedSolvedIssues'),
  [Permissions.MANAGE_ALL_ISSUE]: t('dictionary.permission.manageAllIssues'),
  [Permissions.MANAGE_HARDWARE]: t('dictionary.permission.manageHardware'),
  [Permissions.VIEW_SENSOR_REPORT]: t('dictionary.permission.viewSensorReports'),
  [Permissions.VIEW_ALL_ANALYTICS]: t('dictionary.permission.viewAllAnalytics'),
  [Permissions.MANAGE_OWN_ANALYTICS]: t('dictionary.permission.manageOwnAnalytics'),
  [Permissions.MANAGE_ALL_ANALYTICS]: t('dictionary.permission.manageAllAnalytics'),
  [Permissions.VIEW_OWN_INSPECTION]: t('dictionary.permission.viewOwnInspections'),
  [Permissions.VIEW_ALL_UPCOMING_INSPECTIONS]: t('dictionary.permission.viewAllUpcomingInspections'),
  [Permissions.VIEW_OWN_UPCOMING_INSPECTIONS]: t('dictionary.permission.viewOwnUpcomingInspections'),
  [Permissions.MANAGE_INTEGRATIONS]: t('dictionary.permission.manageIntegrations'),
  [Permissions.APPROVE_ALL_INSPECTIONS]: t('dictionary.permission.approveAllInpsections'),
  [Permissions.APPROVE_ASSIGNED_INSPECTIONS]: t('dictionary.permission.approveAssignedInspections'),
  [Permissions.APPROVE_ALL_ACTIONS]: t('dictionary.permission.approveAllActions'),
  [Permissions.APPROVE_ASSIGNED_ACTIONS]: t('dictionary.permission.approveAssignedActions'),
});

export interface PermissionOption extends SelectOption<string> {group: string}

export const permissionsGrouped = (): (PermissionOption)[] => {
  const toText = permissionsToText();
  const groupAdministration = t('permissions.groups.administration');
  const groupEntities = t('permissions.groups.entities');
  const groupInspections = t('permissions.groups.inspections');
  const groupIssues = t('permissions.groups.issues');
  const groupAnalytics = t('permissions.groups.analytics');
  const groupApprovals = t('permissions.groups.approvals');

  return [
    {
      value: Permissions.MANAGE_BILLING,
      label: toText[Permissions.MANAGE_BILLING],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_USERS,
      label: toText[Permissions.MANAGE_USERS],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_PERMISSIONS,
      label: toText[Permissions.MANAGE_PERMISSIONS],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_CATEGORIES,
      label: toText[Permissions.MANAGE_CATEGORIES],
      group: groupAdministration,
    },
    {
      value: Permissions.VIEW_ALL_ANALYTICS,
      label: toText[Permissions.VIEW_ALL_ANALYTICS],
      group: groupAnalytics,
    },
    {
      value: Permissions.MANAGE_ALL_ANALYTICS,
      label: toText[Permissions.MANAGE_ALL_ANALYTICS],
      group: groupAnalytics,
    },
    {
      value: Permissions.MANAGE_OWN_ANALYTICS,
      label: toText[Permissions.MANAGE_OWN_ANALYTICS],
      group: groupAnalytics,
    },
    {
      value: Permissions.MANAGE_HARDWARE,
      label: toText[Permissions.MANAGE_HARDWARE],
      group: groupAdministration,
    },
    {
      value: Permissions.VIEW_SENSOR_REPORT,
      label: toText[Permissions.VIEW_SENSOR_REPORT],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_CHECKLISTS,
      label: toText[Permissions.MANAGE_CHECKLISTS],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_INTEGRATIONS,
      label: toText[Permissions.MANAGE_INTEGRATIONS],
      group: groupAdministration,
    },
    {
      value: Permissions.MANAGE_ENTITY_ITEMS,
      label: toText[Permissions.MANAGE_ENTITY_ITEMS],
      group: groupEntities,
    },
    {
      value: Permissions.MANAGE_ENTITY_PROPERTIES,
      label: toText[Permissions.MANAGE_ENTITY_PROPERTIES],
      group: groupEntities,
    },
    {
      value: Permissions.MANAGE_ENTITY_FOLDERS,
      label: toText[Permissions.MANAGE_ENTITY_FOLDERS],
      group: groupEntities,
    },
    {
      value: Permissions.MANAGE_ENTITY_TYPES,
      label: toText[Permissions.MANAGE_ENTITY_TYPES],
      group: groupEntities,
    },
    {
      value: Permissions.CONDUCT_OPEN_INSPECTIONS,
      label: toText[Permissions.CONDUCT_OPEN_INSPECTIONS],
      group: groupInspections,
    },
    {
      value: Permissions.MANAGE_DRAFT_INSPECTIONS,
      label: toText[Permissions.MANAGE_DRAFT_INSPECTIONS],
      group: groupInspections,
    },
    {
      value: Permissions.VIEW_ALL_INSPECTION,
      label: toText[Permissions.VIEW_ALL_INSPECTION],
      group: groupInspections,
    },
    {
      value: Permissions.EDIT_ALL_INSPECTION,
      label: toText[Permissions.EDIT_ALL_INSPECTION],
      group: groupInspections,
    },
    {
      value: Permissions.VIEW_OWN_INSPECTION,
      label: toText[Permissions.VIEW_OWN_INSPECTION],
      group: groupInspections,
    },
    {
      value: Permissions.EDIT_OWN_INSPECTION,
      label: toText[Permissions.EDIT_OWN_INSPECTION],
      group: groupInspections,
    },
    {
      value: Permissions.CREATE_ISSUE,
      label: toText[Permissions.CREATE_ISSUE],
      group: groupIssues,
    },
    {
      value: Permissions.CREATE_ISSUE_WITH_ASSIGNEE,
      label: toText[Permissions.CREATE_ISSUE_WITH_ASSIGNEE],
      group: groupIssues,
    },
    {
      value: Permissions.MANAGE_ALL_ISSUE,
      label: toText[Permissions.MANAGE_ALL_ISSUE],
      group: groupIssues,
    },
    {
      value: Permissions.MANAGE_OWN_ISSUE,
      label: toText[Permissions.MANAGE_OWN_ISSUE],
      group: groupIssues,
    },
    {
      value: Permissions.MANAGE_ASSIGNED_SOLVED_ISSUES,
      label: toText[Permissions.MANAGE_ASSIGNED_SOLVED_ISSUES],
      group: groupIssues,
    },
    {
      value: Permissions.VIEW_ALL_UPCOMING_INSPECTIONS,
      label: toText[Permissions.VIEW_ALL_UPCOMING_INSPECTIONS],
      group: groupInspections,
    },
    {
      value: Permissions.VIEW_OWN_UPCOMING_INSPECTIONS,
      label: toText[Permissions.VIEW_OWN_UPCOMING_INSPECTIONS],
      group: groupInspections,
    },
    {
      value: Permissions.APPROVE_ALL_INSPECTIONS,
      label: toText[Permissions.APPROVE_ALL_INSPECTIONS],
      group: groupApprovals,
    },
    {
      value: Permissions.APPROVE_ASSIGNED_INSPECTIONS,
      label: toText[Permissions.APPROVE_ASSIGNED_INSPECTIONS],
      group: groupApprovals,
    },
    {
      value: Permissions.APPROVE_ALL_ACTIONS,
      label: toText[Permissions.APPROVE_ALL_ACTIONS],
      group: groupApprovals,
    },
    {
      value: Permissions.APPROVE_ASSIGNED_ACTIONS,
      label: toText[Permissions.APPROVE_ASSIGNED_ACTIONS],
      group: groupApprovals,
    },
  ];
};

export function permission(perm: Permissions) {
  return getMappedRecord<string>(permissionsToText(), perm);
}

export function apiError(error: ApiError): string {
  if (error.code >= 500 && error.code <= 599) {
    return t('dictionary.api-error.server');
  }

  const map: Record<ApiError['code'], string> = {
    [FETCHING_AUTH_HEADER_ERROR]: t('dictionary.api-error.FETCHING_AUTH_HEADER_ERROR'),
    [NETWORK_ERROR]: t('dictionary.api-error.NETWORK_ERROR'),
    [PARSE_ERROR]: t('dictionary.api-error.PARSE_ERROR'),
    [DECODE_ERROR]: t('dictionary.api-error.DECODE_ERROR'),
    [StatusCodes.UNAUTHORIZED]: t('dictionary.api-error.UNAUTHORIZED'),
    [StatusCodes.FORBIDDEN]: t('dictionary.api-error.FORBIDDEN'),
    [StatusCodes.NOT_FOUND]: t('dictionary.api-error.NOT_FOUND'),
    [StatusCodes.PRECONDITION_FAILED]: t('dictionary.api-error.PRECONDITION_FAILED'),
    [CLIENT_NOT_SUPPORTED]: t('dictionary.api-error.CLIENT_NOT_SUPPORTED'),
    [QUOTA_EXCEEDED]: t('dictionary.api-error.QUOTA_EXCEEDED'),
    [ORGANIZATION_SUSPENDED]: t('dictionary.api-error.ORGANIZATION_SUSPENDED'),
    [HAS_NO_ACTIVE_SUBSCRIPTION]: t('dictionary.api-error.HAS_NO_ACTIVE_SUBSCRIPTION'),
    [SUBSCRIPTION_INCOMPLETE]: t('dictionary.api-error.SUBSCRIPTION_INCOMPLETE'),
    [SUBSCRIPTION_PAST_DUE]: t('dictionary.api-error.SUBSCRIPTION_INCOMPLETE'),
  };

  const errorCodeText: undefined | string = getMappedRecord<ApiError['code']>(map, error.code);

  return errorCodeText && errorCodeText.length > 0
    ? errorCodeText
    : t('dictionary.api-error.unknown');
}
